/**
 * DefinitionGenerator - Generates natural language definitions for individuals.
 *
 * The generated definition follows the pattern:
 * "A [ClassName] that [property1] [value1] and [property2] [value2]..."
 *
 * Only essential (mandatory) properties are included in the generated definition.
 *
 * Renamed from OntologicalDefinitionGenerator as part of the TBox/ABox refactoring.
 */

import Individual from "./Individual.ts";
import KnowledgeGraph from "./KnowledgeGraph.ts";
import { SchemaClass, DataProperty, RestrictedObjectProperty, computeRestrictedObjectProperties } from "@semantical/modules/ontology-model";

/**
 * Determines if a data property is required (has min cardinality >= 1 or exact cardinality >= 1)
 */
function isRequiredDataProperty(prop: DataProperty): boolean {
    if (!prop.cardinality) return false;
    if (prop.cardinality.exact !== undefined && prop.cardinality.exact >= 1) return true;
    if (prop.cardinality.min !== undefined && prop.cardinality.min >= 1) return true;
    return false;
}

/**
 * Determines if an object property restriction is required (min cardinality >= 1 or has hasValue)
 */
function isRequiredObjectPropertyRestriction(restriction: RestrictedObjectProperty): boolean {
    // hasValue restrictions are always "required" in the sense they define a fixed value
    if (restriction.hasValue) return true;
    return restriction.cardinality.min >= 1;
}

/**
 * Common verb prefixes in property labels that indicate the label is already a verb phrase.
 * When a property label starts with one of these, we don't prepend "has".
 */
const VERB_PREFIXES = ['has', 'is', 'was', 'are', 'were', 'can', 'could', 'will', 'would', 'shall', 'should', 'may', 'might', 'must'];

/**
 * Check if a property label already starts with a verb (no need to prepend "has")
 */
function startsWithVerb(label: string): boolean {
    const lowerLabel = label.toLowerCase();
    return VERB_PREFIXES.some(prefix =>
        lowerLabel.startsWith(prefix + ' ') || lowerLabel === prefix
    );
}

/**
 * Generates natural language definitions for individuals based on their ontological properties.
 */
export default class DefinitionGenerator {
    private knowledgeGraph: KnowledgeGraph | null;

    constructor(knowledgeGraph: KnowledgeGraph | null = null) {
        this.knowledgeGraph = knowledgeGraph;
    }

    /**
     * Set the knowledge graph for resolving relationships
     */
    setKnowledgeGraph(knowledgeGraph: KnowledgeGraph | null): void {
        this.knowledgeGraph = knowledgeGraph;
    }

    /**
     * Generate a natural language definition for an individual based on its essential properties.
     *
     * @param schemaClass The SchemaClass containing property definitions
     * @param individual The individual to generate a definition for
     * @returns A natural language definition string, or null if no essential properties have values
     */
    generate(schemaClass: SchemaClass, individual: Individual): string | null {
        const dataProperties = schemaClass.dataProperties || [];
        const objectPropertyRestrictions = computeRestrictedObjectProperties({ subClassOf: schemaClass._subClassOf });

        // Get essential (mandatory) properties only
        const mandatoryDataProps = dataProperties.filter(isRequiredDataProperty);
        const mandatoryObjectProps = objectPropertyRestrictions.filter(isRequiredObjectPropertyRestriction);

        const parts: string[] = [];

        // Add data property values
        for (const prop of mandatoryDataProps) {
            const part = this.formatDataPropertyValue(prop, individual);
            if (part) {
                parts.push(part);
            }
        }

        // Add object property values (including hasValue restrictions)
        for (const restriction of mandatoryObjectProps) {
            const part = this.formatObjectPropertyValue(restriction, individual);
            if (part) {
                parts.push(part);
            }
        }

        if (parts.length === 0) {
            return null;
        }

        // Build the definition: "A [ClassName] that [parts joined by ' and ']"
        const className = schemaClass.label || schemaClass.name;
        return `A ${className} that ${parts.join(' and ')}.`;
    }

    /**
     * Format a data property value for inclusion in the definition
     */
    private formatDataPropertyValue(prop: DataProperty, individual: Individual): string | null {
        const values = individual.getPropertyValues(prop.uri);
        if (values.length === 0) {
            return null;
        }

        const propLabel = prop.label.toLowerCase();
        // Only prepend "has" if the label doesn't already start with a verb
        const propPhrase = startsWithVerb(propLabel) ? propLabel : `has ${propLabel}`;

        if (values.length === 1) {
            return `${propPhrase} "${values[0]}"`;
        }

        // Multiple values: "has name "A", "B" and "C""
        const formattedValues = values.map(v => `"${v}"`);
        const lastValue = formattedValues.pop();
        const valueStr = formattedValues.length > 0
            ? `${formattedValues.join(', ')} and ${lastValue}`
            : lastValue;
        return `${propPhrase} ${valueStr}`;
    }

    /**
     * Format an object property value for inclusion in the definition.
     * Only includes ABox-declared relationships, not TBox hasValue restrictions.
     */
    private formatObjectPropertyValue(
        restriction: RestrictedObjectProperty,
        individual: Individual
    ): string | null {
        // Skip hasValue restrictions - they are inherited from TBox (class definition)
        if (restriction.hasValue) {
            return null;
        }

        // Check for ABox-declared relationships only
        if (!this.knowledgeGraph) {
            return null;
        }

        const relationships = Array.from(this.knowledgeGraph.relationships.values()).filter(
            r => r.subject === individual && r.predicate.uri === restriction.uri
        );

        if (relationships.length === 0) {
            return null;
        }

        const propLabel = restriction.label.toLowerCase();
        // Only prepend "has" if the label doesn't already start with a verb
        const propPhrase = startsWithVerb(propLabel) ? propLabel : `has ${propLabel}`;
        const targetNames = relationships.map(r => `"${r.object.name}"`);

        if (targetNames.length === 1) {
            return `${propPhrase} ${targetNames[0]}`;
        }

        const lastTarget = targetNames.pop();
        const targetStr = targetNames.length > 0
            ? `${targetNames.join(', ')} and ${lastTarget}`
            : lastTarget;
        return `${propPhrase} ${targetStr}`;
    }

    /**
     * Generate Turtle format export containing only essential (mandatory) properties.
     */
    toTurtle(schemaClass: SchemaClass, individual: Individual): string {
        // Known prefixes (namespace -> prefix)
        const knownPrefixes: Record<string, string> = {
            'http://www.w3.org/1999/02/22-rdf-syntax-ns#': 'rdf',
            'http://www.w3.org/2000/01/rdf-schema#': 'rdfs',
            'http://www.w3.org/2002/07/owl#': 'owl',
            'http://www.w3.org/2001/XMLSchema#': 'xsd',
        };

        // Collect which prefixes are actually used
        const usedPrefixes = new Set<string>(['rdf', 'rdfs', 'owl']);

        // Helper to shorten a URI using known prefixes
        const formatUri = (uri: string): string => {
            for (const [ns, prefix] of Object.entries(knownPrefixes)) {
                if (uri.startsWith(ns)) {
                    usedPrefixes.add(prefix);
                    return `${prefix}:${uri.substring(ns.length)}`;
                }
            }
            return `<${uri}>`;
        };

        // Helper to escape string literals
        const escapeString = (s: string) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

        const dataProperties = schemaClass.dataProperties || [];
        const objectPropertyRestrictions = computeRestrictedObjectProperties({ subClassOf: schemaClass._subClassOf });

        // Get essential (mandatory) properties only
        const mandatoryDataProps = dataProperties.filter(isRequiredDataProperty);
        const mandatoryObjectProps = objectPropertyRestrictions.filter(isRequiredObjectPropertyRestriction);

        // Content lines (will prepend prefixes later)
        const contentLines: string[] = [];

        // Header comment
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push(`# Essential Properties: ${individual.name}`);
        contentLines.push(`# Instance of: ${schemaClass.label || schemaClass.name}`);
        contentLines.push(`# Exported: ${new Date().toISOString()}`);
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push('');

        // Build the individual's triples
        const props: string[] = [];

        // Type declarations
        props.push(`    a owl:NamedIndividual ,`);
        props.push(`      ${formatUri(schemaClass.uri)} ;`);

        // Label
        props.push(`    rdfs:label "${escapeString(individual.name)}"`);

        // Essential data property values
        for (const prop of mandatoryDataProps) {
            const values = individual.getPropertyValues(prop.uri);
            if (values.length === 0) continue;

            // Find the property range/datatype
            const range = prop.range?.toLowerCase() || 'string';

            for (const value of values) {
                props[props.length - 1] += ' ;';

                let formattedValue: string;
                if (range === 'date' || range === 'datetime') {
                    formattedValue = `"${escapeString(value)}"^^xsd:date`;
                    usedPrefixes.add('xsd');
                } else if (range === 'integer' || range === 'int' || range === 'long' || range === 'short') {
                    formattedValue = `"${escapeString(value)}"^^xsd:integer`;
                    usedPrefixes.add('xsd');
                } else if (range === 'decimal' || range === 'float' || range === 'double') {
                    formattedValue = `"${escapeString(value)}"^^xsd:decimal`;
                    usedPrefixes.add('xsd');
                } else if (range === 'boolean') {
                    formattedValue = `"${value === 'true' ? 'true' : 'false'}"^^xsd:boolean`;
                    usedPrefixes.add('xsd');
                } else {
                    formattedValue = `"${escapeString(value)}"`;
                }

                props.push(`    ${formatUri(prop.uri)} ${formattedValue}`);
            }
        }

        // Collect target individuals for later export
        const targetIndividuals: Individual[] = [];

        // Essential object property values (ABox-declared only, skip hasValue restrictions)
        for (const restriction of mandatoryObjectProps) {
            // Skip hasValue restrictions - they are inherited from TBox
            if (restriction.hasValue) continue;

            if (!this.knowledgeGraph) continue;

            const rels = Array.from(this.knowledgeGraph.relationships.values()).filter(
                r => r.subject === individual && r.predicate.uri === restriction.uri
            );

            for (const rel of rels) {
                props[props.length - 1] += ' ;';
                props.push(`    ${formatUri(restriction.uri)} <${rel.object.uri}>`);
                // Collect target individual for export
                if (rel.object instanceof Individual) {
                    targetIndividuals.push(rel.object);
                }
            }
        }

        contentLines.push(`<${individual.uri}>`);
        props[props.length - 1] += ' .';
        contentLines.push(...props);
        contentLines.push('');

        // Export target individuals (label and type only)
        if (targetIndividuals.length > 0) {
            contentLines.push('# -----------------------------------------------------------');
            contentLines.push('# Referenced Individuals');
            contentLines.push('# -----------------------------------------------------------');
            contentLines.push('');

            // Use a Set to avoid duplicates (by URI)
            const exportedUris = new Set<string>();
            for (const target of targetIndividuals) {
                if (exportedUris.has(target.uri)) continue;
                exportedUris.add(target.uri);

                const targetProps: string[] = [];
                targetProps.push(`    a owl:NamedIndividual ,`);
                targetProps.push(`      ${formatUri(target.modelClass.uri)} ;`);
                targetProps.push(`    rdfs:label "${escapeString(target.name)}" .`);

                contentLines.push(`<${target.uri}>`);
                contentLines.push(...targetProps);
                contentLines.push('');
            }
        }

        // Build prefix declarations (only for used prefixes)
        const prefixLines: string[] = [];
        const nsToPrefix = Object.entries(knownPrefixes);
        // Sort by prefix name for consistent output
        nsToPrefix.sort((a, b) => a[1].localeCompare(b[1]));

        for (const [ns, prefix] of nsToPrefix) {
            if (usedPrefixes.has(prefix)) {
                // Pad prefix for alignment
                const paddedPrefix = (prefix + ':').padEnd(10);
                prefixLines.push(`@prefix ${paddedPrefix}<${ns}> .`);
            }
        }
        prefixLines.push('');

        return [...prefixLines, ...contentLines].join('\n');
    }
}
